import React, { useEffect, useMemo, useState } from 'react';
import { authToken } from '../../authToken';
import { API_BASE_URL } from '../../config';
import ModalShell from '../../components/ModalShell';
import FoodIntakeTemplateAddPanel from './FoodIntakeTemplateAddPanel';
import FoodIntakeTemplateEditPanel from './FoodIntakeTemplateEditPanel';
import FoodIntakeTemplateItemMenuPanel from './FoodIntakeTemplateItemMenuPanel';

const ENDPOINT_HOST = API_BASE_URL;
const ENDPOINT_TEMPLATES = `${ENDPOINT_HOST}/foodintaketemplate`;

const sortByNameAsc = (arr) => {
  const a = Array.isArray(arr) ? [...arr] : [];
  a.sort((x, y) => {
    const xb = String(x?.brand ?? '').toLowerCase();
    const yb = String(y?.brand ?? '').toLowerCase();
    if (xb !== yb) return xb.localeCompare(yb);
    const xn = String(x?.name ?? '').toLowerCase();
    const yn = String(y?.name ?? '').toLowerCase();
    return xn.localeCompare(yn);
  });
  return a;
};

export default function FoodIntakeTemplateListPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeSubPanel, setActiveSubPanel] = useState(null); // 'add'
  const [activeItem, setActiveItem] = useState(null);
  const [showMenuForId, setShowMenuForId] = useState(null);
  const [showEditForItem, setShowEditForItem] = useState(null);
  const [confirmDeleteForItem, setConfirmDeleteForItem] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(ENDPOINT_TEMPLATES, {
        headers: {
          ...(authToken ? { 'Authorization': authToken } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch templates (${response.status})`);
      }

      const data = await response.json();
      setItems(sortByNameAsc(data));
    } catch (e) {
      console.error('Failed to fetch templates:', e);
      setError(e.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const sortedItems = useMemo(() => sortByNameAsc(items), [items]);

  const nonDuplicatedBrandItemCount = useMemo(() => {
    const counts = new Map();
    for (const it of sortedItems) {
      const br = String(it?.brand ?? '').trim().toLowerCase();
      if (!br) continue;
      counts.set(br, (counts.get(br) || 0) + 1);
    }

    let cnt = 0;
    for (const it of sortedItems) {
      const br = String(it?.brand ?? '').trim().toLowerCase();
      if (!br) continue;
      if ((counts.get(br) || 0) === 1) cnt += 1;
    }
    return cnt;
  }, [sortedItems]);

  const openMenu = (item) => {
    setActiveItem(item);
    setShowMenuForId(item?.id ?? '__noid__');
  };

  const closeMenu = () => {
    setShowMenuForId(null);
    setActiveItem(null);
  };

  const handleDelete = async (item) => {
    const id = item?.id;
    if (id === null || id === undefined) return;

    try {
      const response = await fetch(`${ENDPOINT_TEMPLATES}/${id}`, {
        method: 'DELETE',
        headers: {
          ...(authToken ? { 'Authorization': authToken } : {})
        }
      });

      if (!response.ok) {
        alert(`Failed to delete (${response.status})`);
        return;
      }

      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      console.error('Failed to delete template:', e);
      alert('Failed to delete');
    }
  };

  const itemCardStyle = {
    padding: '10px 12px',
    margin: '6px 0',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: 10,
    alignItems: 'center'
  };

  const renderTitle = (it) => {
    const nm = it?.name || '(no name)';
    const br = (it?.brand || '').trim();
    return br ? `${br} — ${nm}` : nm;
  };

  return (
    <div style={{ padding: '12px 0' }}>
      <h3 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>📦 Food Templates</h3>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <button
          style={{
            flex: 1,
            padding: '12px 16px',
            backgroundColor: '#ff8a00',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={() => setActiveSubPanel('add')}
        >
          ➕ Add
        </button>
        <button
          style={{
            flex: 1,
            padding: '12px 16px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer'
          }}
          onClick={fetchItems}
        >
          ↻ Refresh
        </button>
      </div>

      <div
        style={{
          textAlign: 'center',
          margin: '-4px 0 10px 0',
          fontWeight: 800,
          fontSize: 13,
          color: nonDuplicatedBrandItemCount === 0 ? '#2e7d32' : '#c62828'
        }}
      >
        Brand errors: {nonDuplicatedBrandItemCount}
      </div>

      {loading && <div style={{ textAlign: 'center', color: '#666' }}>Loading...</div>}
      {error && <div style={{ textAlign: 'center', color: 'red' }}>Error: {error}</div>}

      {!loading && !error && sortedItems.length === 0 && activeSubPanel !== 'add' && (
        <div style={{ textAlign: 'center', color: '#666', padding: '10px 0' }}>
          No templates yet. Use “Add”.
        </div>
      )}

      {activeSubPanel === 'add' && (
        <ModalShell height={620} width={560}>
          <FoodIntakeTemplateAddPanel
            onClose={(changed) => {
              setActiveSubPanel(null);
              if (changed) fetchItems();
            }}
          />
        </ModalShell>
      )}

      {!loading && !error && sortedItems.length > 0 && (
        <div style={{ maxHeight: '420px', overflowY: 'auto', paddingTop: 6 }}>
          {sortedItems.map((it) => (
            <div key={it.id ?? `${it.brand}-${it.name}`} style={itemCardStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {renderTitle(it)}
                </div>

                <div style={{ marginTop: 6, color: '#333', fontSize: 13, display: 'flex', flexWrap: 'wrap', gap: 10 }}>                  
                  <span><b>S</b>: {it.sugars}</span>
                  <span><b>C</b>: {it.carbsWithSugar}</span>                  
                  <span><b>P</b>: {it.protein}</span>
                  <span><b>F</b>: {it.fat}</span>                  
                  <span><b>kcal</b>: {it.kcal}</span>
                  <span><b>reference</b>: {it.refUnits} {it.unitName}</span>
                  <span><b>portion</b>: {it.defaultPortion} {it.unitName}</span>
                </div>
              </div>

              <button
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 22,
                  cursor: 'pointer',
                  color: '#888',
                  padding: '4px 6px'
                }}
                aria-label="Menu"
                onClick={() => openMenu(it)}
              >
                ⋯
              </button>
            </div>
          ))}
        </div>
      )}

      {showMenuForId && activeItem && (
        <FoodIntakeTemplateItemMenuPanel
          onEdit={() => {
            setShowEditForItem(activeItem);
            closeMenu();
          }}
          onDelete={() => {
            setConfirmDeleteForItem(activeItem);
            closeMenu();
          }}
          onCancel={closeMenu}
        />
      )}

      {showEditForItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            maxWidth: '700px',
            width: '92%',
            maxHeight: '92vh',
            overflow: 'auto'
          }}>
            <FoodIntakeTemplateEditPanel
              item={showEditForItem}
              onClose={(changed) => {
                setShowEditForItem(null);
                if (changed) fetchItems();
              }}
            />
          </div>
        </div>
      )}

      {confirmDeleteForItem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 20000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            maxWidth: '340px',
            width: '90%'
          }}>
            <h4 style={{ margin: '0 0 16px 0', textAlign: 'center' }}>Delete Template</h4>
            <p style={{ margin: '0 0 20px 0', textAlign: 'center', fontSize: '14px' }}>
              Delete <strong>{renderTitle(confirmDeleteForItem)}</strong>?
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setConfirmDeleteForItem(null)}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const it = confirmDeleteForItem;
                  setConfirmDeleteForItem(null);
                  await handleDelete(it);
                }}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
